import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * DependencyWarningModal - Warns user when editing a level will affect later levels
 *
 * Shows:
 * - Which levels will be affected
 * - What dependencies exist
 * - Options to proceed or cancel
 */
function DependencyWarningModal({
  isOpen,
  onClose,
  onConfirm,
  editingLevel,
  affectedLevels,
  levelsToReset
}) {
  if (!isOpen) return null;

  const hasResets = levelsToReset.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-surface-1 rounded-lg border border-border-default max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-border-default">
          <div className="flex items-start gap-3">
            <AlertTriangle className={cn(
              "w-6 h-6 flex-shrink-0 mt-0.5",
              hasResets ? "text-red-400" : "text-yellow-400"
            )} />
            <div>
              <h2 className="text-heading-md text-gray-100 mb-1">
                {hasResets ? 'Warning: This Will Reset Later Levels' : 'Changes May Affect Later Levels'}
              </h2>
              <p className="text-small text-gray-400">
                Editing Level {editingLevel} will impact your choices at later levels
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Affected Levels */}
        <div className="p-6 space-y-4">
          {Object.entries(affectedLevels).map(([level, reasons]) => (
            <div
              key={level}
              className={cn(
                "p-4 rounded-lg border",
                levelsToReset.includes(parseInt(level))
                  ? "bg-red-500/10 border-red-500/50"
                  : "bg-yellow-500/10 border-yellow-500/50"
              )}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-heading-sm text-gray-100">
                  Level {level}
                  {levelsToReset.includes(parseInt(level)) && (
                    <span className="ml-2 text-tiny text-red-400 font-medium">
                      Will be reset
                    </span>
                  )}
                </h3>
              </div>

              <ul className="space-y-2">
                {reasons.map((reason, idx) => (
                  <li key={idx} className="flex gap-2 text-small text-gray-300">
                    <span className="text-primary flex-shrink-0">•</span>
                    <span>{reason.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Explanation */}
        <div className="px-6 pb-6">
          {hasResets ? (
            <div className="bg-surface-2 rounded-lg p-4 border border-border-default">
              <h4 className="text-heading-sm text-gray-100 mb-2">What happens if you proceed:</h4>
              <ul className="space-y-1 text-small text-gray-400">
                <li className="flex gap-2">
                  <span className="text-red-400">•</span>
                  Level {editingLevel} will be saved with your new choices
                </li>
                <li className="flex gap-2">
                  <span className="text-red-400">•</span>
                  Levels {levelsToReset.join(', ')} will be reset to incomplete
                </li>
                <li className="flex gap-2">
                  <span className="text-red-400">•</span>
                  You'll need to re-make advancement choices for those levels
                </li>
                <li className="flex gap-2">
                  <span className="text-red-400">•</span>
                  Your character's current stats will be recalculated from Level 1 to {editingLevel}
                </li>
              </ul>
            </div>
          ) : (
            <div className="bg-surface-2 rounded-lg p-4 border border-border-default">
              <h4 className="text-heading-sm text-gray-100 mb-2">What happens if you proceed:</h4>
              <ul className="space-y-1 text-small text-gray-400">
                <li className="flex gap-2">
                  <span className="text-yellow-400">•</span>
                  Level {editingLevel} will be saved with your new choices
                </li>
                <li className="flex gap-2">
                  <span className="text-yellow-400">•</span>
                  Later levels will remain intact
                </li>
                <li className="flex gap-2">
                  <span className="text-yellow-400">•</span>
                  Review affected levels to ensure everything still makes sense
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-6 border-t border-border-default bg-surface-2">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-surface-1 text-gray-300 rounded-lg hover:bg-surface-3 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={cn(
              "px-6 py-3 rounded-lg font-medium transition-colors",
              hasResets
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-primary text-white hover:bg-primary-dark"
            )}
          >
            {hasResets ? 'Proceed and Reset Levels' : 'Proceed with Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DependencyWarningModal;
